import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useTheme as useMuiTheme,
} from '@mui/material';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';

import { SidebarNavList } from '@app/components/sidebar/SidebarNavList';
import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
  useSidebarState,
} from '@app/components/sidebar/SidebarStateProvider';
import { AccountMenu } from '@modules/_shared/components/AccountMenu';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { RealAdvisorLogo } from '@shared/components/RealAdvisorLogo';
import { useLocale } from '@shared/hooks/locale';
import { TOP_BAR_HEIGHT } from '@shared/theme/layout';
import { useTheme } from '@shared/theme/theme';
import { isRATenantId } from '@shared/utils/tenant';

const TenantBranding = () => {
  const { me } = useAppData();
  const { colors } = useTheme();
  const isRATenant = isRATenantId(me?.tenant_id ?? '');
  const tenantName = me?.tenant.name;
  const logoUrl = me?.tenant.image?.url;

  if (isRATenant) {
    return (
      // 8px header padding + 7px margin puts the ~25px round mark's center
      // on the 28px rail axis shared by the nav icons and the avatar
      <RealAdvisorLogo style={{ width: 150, marginLeft: 7 }} />
    );
  }
  if (logoUrl != null) {
    return (
      <img
        src={logoUrl}
        alt="logo"
        style={{
          height: 48,
          borderRadius: 9,
          marginLeft: 4,
          width: 'calc(100% - 56px)',
          objectFit: 'contain',
          background: 'white',
        }}
      />
    );
  }
  return (
    <Typography
      variant="body1"
      noWrap
      sx={{ color: colors.highText, ml: 1.5, fontWeight: 500 }}
    >
      {tenantName}
    </Typography>
  );
};

const SidebarHeader = ({ collapsed }: { collapsed: boolean }) => {
  const { t } = useLocale();
  const { isMobile, toggleMode, closeMobile } = useSidebarState();

  if (collapsed) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          // same height as the expanded header so the nav list does not
          // shift vertically when the sidebar is toggled; the border is
          // drawn inside so the line matches the top bar's bottom border
          height: TOP_BAR_HEIGHT,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton
          title={t('openSideMenu')}
          color="inherit"
          onClick={toggleMode}
        >
          <PanelLeftOpen size={20} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        // border drawn inside so the line matches the top bar's bottom
        // border, which is drawn inside its own height as well
        height: TOP_BAR_HEIGHT,
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 1,
      }}
    >
      <TenantBranding />
      <Box sx={{ flexGrow: 1 }} />
      <IconButton
        title={t('closeSideMenu')}
        color="inherit"
        onClick={isMobile ? closeMobile : toggleMode}
      >
        <PanelLeftClose size={20} />
      </IconButton>
    </Box>
  );
};

const SidebarContent = ({ collapsed }: { collapsed: boolean }) => (
  <>
    <SidebarHeader collapsed={collapsed} />
    <Box
      sx={theme => ({
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        overflowX: 'hidden',
        // momentum scrolling on iOS
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0, 0, 0, 0.3) transparent',
        ...theme.applyStyles('dark', {
          scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
        }),
      })}
    >
      <SidebarNavList collapsed={collapsed} />
    </Box>
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        // 52px total with the border inside, mirroring the data grids'
        // 52px footer (pagination) so the two separator lines align
        height: 52,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <AccountMenu collapsed={collapsed} sx={{ width: '100%' }} />
    </Box>
  </>
);

/**
 * Application sidebar with three states:
 * - desktop expanded: permanent drawer, 256px
 * - desktop collapsed: permanent mini variant, 56px, second level in popovers
 * - mobile: hidden behind the top bar hamburger, temporary drawer
 */
export const AppSidebar = () => {
  const { mode, isMobile, mobileOpen, closeMobile } = useSidebarState();
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();

  const collapsed = !isMobile && mode === 'collapsed';
  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  const widthTransition = muiTheme.transitions.create('width', {
    easing: muiTheme.transitions.easing.sharp,
    duration: muiTheme.transitions.duration.enteringScreen,
  });

  const paperSx = {
    width,
    overflowX: 'hidden',
    backgroundColor: colors.white,
    backgroundImage: 'none',
    color: colors.mediumText,
    borderRight: `1px solid ${colors.divider}`,
    display: 'flex',
    flexDirection: 'column',
    transition: widthTransition,
  } as const;

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeMobile}
        // better open performance on mobile
        ModalProps={{ keepMounted: true }}
        slotProps={{ paper: { sx: paperSx } }}
      >
        <SidebarContent collapsed={false} />
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open={true}
      sx={{
        width,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        transition: widthTransition,
      }}
      slotProps={{ paper: { sx: paperSx } }}
    >
      <SidebarContent collapsed={collapsed} />
    </Drawer>
  );
};
